import type { ParsedQs } from 'qs'

import { getActiveChainId } from './active'
import { isNativeCurrencySymbol, nativeCurrencyId } from './native'
import { isAddress } from '../utils'

/** Parsea una pata de par (token0/token1) desde query o path segment. */
export function parsePairLegFromURLParameter(urlParam: unknown): string {
  const chainId = getActiveChainId()
  const nativeId = nativeCurrencyId(chainId)
  if (typeof urlParam === 'string') {
    const valid = isAddress(urlParam)
    if (valid) return valid
    if (isNativeCurrencySymbol(urlParam, chainId)) return nativeId
    if (valid === false) return nativeId
  }
  return nativeId
}

export function readPairLegQueryParams(parsedQs: ParsedQs): {
  token0Id: string | undefined
  token1Id: string | undefined
} {
  return {
    token0Id:
      typeof parsedQs.token0 === 'string'
        ? parsePairLegFromURLParameter(parsedQs.token0)
        : undefined,
    token1Id:
      typeof parsedQs.token1 === 'string'
        ? parsePairLegFromURLParameter(parsedQs.token1)
        : undefined,
  }
}

export function hasPairLegQueryParams(parsedQs: ParsedQs): boolean {
  return typeof parsedQs.token0 === 'string' || typeof parsedQs.token1 === 'string'
}

export function buildPairLegQueryString(token0Id: string, token1Id: string): string {
  return new URLSearchParams({ token0: token0Id, token1: token1Id }).toString()
}

/** Comparación estable de IDs de pata (misma semántica que swap). */
export function pairLegIdsEquivalent(a: string | undefined, b: string | undefined): boolean {
  if (a === b) return true
  if (!a || !b) return false

  const chainId = getActiveChainId()
  const nativeId = nativeCurrencyId(chainId)
  const aNative = a === nativeId || isNativeCurrencySymbol(a, chainId)
  const bNative = b === nativeId || isNativeCurrencySymbol(b, chainId)
  if (aNative && bNative) return true

  const addrA = isAddress(a)
  const addrB = isAddress(b)
  if (addrA && addrB) return addrA.toLowerCase() === addrB.toLowerCase()

  return a.toLowerCase() === b.toLowerCase()
}

/** Para futura sync URL↔estado en liquidez: comparación semántica, no solo string exacto. */
export function pairLegQueryMatchesIds(
  search: string,
  token0Id: string,
  token1Id: string,
  parsedToken0Id: string | undefined,
  parsedToken1Id: string | undefined,
): boolean {
  const expected = `?${buildPairLegQueryString(token0Id, token1Id)}`
  if (search === expected) return true
  if (!search || search.length <= 1) return false

  return (
    pairLegIdsEquivalent(parsedToken0Id, token0Id) &&
    pairLegIdsEquivalent(parsedToken1Id, token1Id)
  )
}

/** Detalle off-catalog: solo query `token0`/`token1`, sin slug en path. */
export function buildOffCatalogPoolDetailPath(token0Id: string, token1Id: string): string {
  return `/liquidity?${buildPairLegQueryString(token0Id, token1Id)}`
}
